import React, { useState, useEffect, useMemo } from "react";
import "./Cloud9.css";

const cloud9Plans = [
  {
    id: 6,
    name: "Silver",
    description: "Entry-level subscription plan",
    aiAskLimit: 100,
    aiResearchLimit: 2,
    subscriptionFee: 4.99,
    postLimit: 500,
    mediaLimit: 4096,
    editTimeout: 30,
    priorityBoost: true,
    paypalPlanId: "P-0JH73683AW042032LNKXQDLQ",
    isAvailable: true,
  },
  {
    id: 7,
    name: "Gold",
    description: "Mid-tier subscription plan",
    aiAskLimit: 300,
    aiResearchLimit: 10,
    subscriptionFee: 7.99,
    postLimit: 1000,
    mediaLimit: 8192,
    editTimeout: 60,
    priorityBoost: true,
    paypalPlanId: "P-8FS09903SP9316003NKXQKSQ",
    isAvailable: true,
  },
  {
    id: 8,
    name: "Platinum",
    description: "High-tier plan with boosts",
    aiAskLimit: 750,
    aiResearchLimit: 25,
    subscriptionFee: 11.99,
    postLimit: 2000,
    mediaLimit: 16384,
    editTimeout: 120,
    priorityBoost: true,
    paypalPlanId: "P-01M5338393508891GNKXQLRI",
    isAvailable: true,
  },
  {
    id: 9,
    name: "Diamond",
    description: "Top-tier premium plan",
    aiAskLimit: 1500,
    aiResearchLimit: 40,
    subscriptionFee: 19.99,
    postLimit: 3000,
    mediaLimit: 20480,
    editTimeout: 180,
    priorityBoost: true,
    paypalPlanId: "P-2HT74804AM390580CNKXQMYI",
    isAvailable: true,
  },
];

// DEV ONLY:
// Replace this placeholder locally with the Sandbox Client ID from
// the same PayPal Sandbox application configured in the DEV backend .env.
const PAYPAL_CLIENT_ID = "AesC9rlsAgMzvmvtsnd5jte2uPvF-FfaQhzb9HBkImi8LWWrYqvo_xB26rurV9SiY6Evg4CTaOgsGtra"

const Cloud9 = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmingSubscription, setConfirmingSubscription] =
    useState(false);

  // ✅ Determine current user + guest state (safe parsing)
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  // ✅ Treat as guest if:
  // - no currentUser
  // - or flags
  // - or group_id 0 (common guest pattern)
  const isGuest = useMemo(() => {
    if (!currentUser) return true;
    if (currentUser.isGuest === true) return true;
    if (currentUser.guest === true) return true;
    if (Number(currentUser.group_id) === 0) return true;
    return false;
  }, [currentUser]);

  // ✅ Text filtering across plan name + description
  const filteredPlans = useMemo(() => {
    const lower = (searchTerm || "").toLowerCase();
    return cloud9Plans.filter((plan) => {
      return (
        plan.name.toLowerCase().includes(lower) ||
        plan.description.toLowerCase().includes(lower)
      );
    });
  }, [searchTerm]);

  // ✅ Load PayPal SDK ONLY if not guest
  useEffect(() => {
    if (isGuest) return;

    const scriptId = "paypal-sdk";

    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");

      script.src =
        `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}` +
        "&vault=true&intent=subscription";

      script.id = scriptId;
      script.async = true;

      script.onload = () => {
        console.log("✅ PayPal SDK loaded");
      };

      script.onerror = () => {
        console.error("❌ Failed to load PayPal SDK");
      };

      document.body.appendChild(script);
    }
  }, [isGuest]);

  // ✅ Render PayPal buttons only for non-guest
  useEffect(() => {
    if (isGuest) return;

    if (
      showPayPal &&
      selectedPlan?.paypalPlanId &&
      window.paypal &&
      currentUser
    ) {
      const container = document.getElementById(
        "paypal-button-container"
      );

      if (container) {
        container.innerHTML = "";

        window.paypal
          .Buttons({
            createSubscription: (_data, actions) => {
              const userId = Number(currentUser.id);

              if (!Number.isInteger(userId) || userId <= 0) {
                throw new Error(
                  "A valid Scoldy user ID is required to create a subscription."
                );
              }

              return actions.subscription.create({
                plan_id: selectedPlan.paypalPlanId,

                // Bind the PayPal subscription itself to the Scoldy user.
                // The backend independently verifies this value with PayPal.
                custom_id: String(userId),
              });
            },

            onApprove: async (data) => {
              const subscriptionId = String(
                data?.subscriptionID || ""
              ).trim();

              const userId = Number(currentUser.id);

              if (!subscriptionId) {
                console.error(
                  "❌ PayPal approval did not return a subscription ID."
                );

                alert(
                  "PayPal approved the transaction, but no subscription ID was returned. Please contact support."
                );

                return;
              }

              if (!Number.isInteger(userId) || userId <= 0) {
                console.error(
                  "❌ Unable to confirm subscription: invalid Scoldy user ID."
                );

                alert(
                  "Your PayPal subscription was created, but Scoldy could not identify your account. Please contact support."
                );

                return;
              }

              try {
                setConfirmingSubscription(true);

                const response = await fetch(
                  "/backend/paypal/subscription/confirm",
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      user_id: userId,
                      subscription_id: subscriptionId,
                    }),
                  }
                );

                let result = null;

                try {
                  result = await response.json();
                } catch {
                  result = null;
                }

                if (!response.ok || !result?.success) {
                  const errorMessage =
                    result?.error ||
                    "Scoldy could not confirm the PayPal subscription.";

                  throw new Error(errorMessage);
                }

                // Keep the locally stored user in sync when the backend
                // actually changed the subscription group.
                if (
                  result.groupChanged === true &&
                  result.subscription?.group_id
                ) {
                  const updatedUser = {
                    ...currentUser,
                    group_id: Number(
                      result.subscription.group_id
                    ),
                  };

                  localStorage.setItem(
                    "currentUser",
                    JSON.stringify(updatedUser)
                  );
                }

                container.innerHTML = "";
                setShowPayPal(false);

                if (result.blocked === true) {
                  alert(
                    `Your PayPal subscription was confirmed successfully. Subscription ID: ${subscriptionId}`
                  );
                } else if (
                  result.subscription?.status === "ACTIVE"
                ) {
                  alert(
                    `🎉 Your ${selectedPlan.name} subscription is active!`
                  );
                } else {
                  alert(
                    `Your PayPal subscription was created successfully and is currently ${result.subscription?.status || "being processed"}.`
                  );
                }
              } catch (err) {
                console.error(
                  "❌ Scoldy subscription confirmation error:",
                  err
                );

                alert(
                  "Your PayPal subscription may have been created, but Scoldy could not confirm it. Please do not create another subscription. Contact support with your PayPal subscription information."
                );
              } finally {
                setConfirmingSubscription(false);
              }
            },

            onError: (err) => {
              console.error("❌ PayPal Error:", err);

              alert(
                "❌ An error occurred with PayPal. The subscription was not confirmed by Scoldy."
              );
            },
          })
          .render("#paypal-button-container")
          .catch((err) => {
            console.error(
              "❌ PayPal button rendering error:",
              err
            );
          });
      }
    }
  }, [
    isGuest,
    showPayPal,
    selectedPlan,
    currentUser,
  ]);

  const handleSubscribe = () => {
    // ✅ Guests cannot subscribe (hard stop)
    if (isGuest) {
      alert(
        "Guests can view plans, but must log in to subscribe."
      );
      return;
    }

    if (!selectedPlan) {
      alert("Please select a plan to subscribe.");
    } else if (!selectedPlan.isAvailable) {
      alert(
        `🚧 The ${selectedPlan.name} plan is coming soon!`
      );
    } else if (!selectedPlan.paypalPlanId) {
      alert(
        `🚧 PayPal integration is not yet available for the ${selectedPlan.name} plan.`
      );
    } else {
      setShowPayPal(true);
    }
  };

  return (
    <div className="cloud9-container">
      <h1 className="cloud9-header">Premium Plans</h1>

      <p className="cloud9-subtitle">
        Compare our tiers and pick the one that fits you best!
      </p>

      {/* ✅ Guest banner */}
      {isGuest && (
        <div
          style={{
            margin: "12px auto 18px",
            maxWidth: 820,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <strong>Guest mode:</strong> You can view plans, but
          you must log in to subscribe.
        </div>
      )}

      <div className="cloud9-basic-account">
        <strong>Free Basic Account</strong>
        <div>
          Scoldy's Basic account is free and includes access to Scoldy's communities and core social features, with posts up to 280 characters. Upgrade to Premium for longer posts, media uploads, post editing, monthly Priority Boosts, Ask Scoldy AI and Scoldy AI Research.
        </div>
      </div>

      {/* ✅ Search input */}
      <div className="cloud9-search">
        <input
          type="text"
          className="cloud9-search-box"
          placeholder="Search plans..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* ✅ Grid layout preserved */}
      <div className="cloud9-plan-grid">
        {filteredPlans.map((plan) => {
          const annualPrice = (
            plan.subscriptionFee * 11
          ).toFixed(2);

          const discount = (
            (1 - 11 / 12) *
            100
          ).toFixed(0);

          const clickable = !isGuest;

          return (
            <div
              key={plan.id}
              className={`cloud9-plan-box ${
                selectedPlan?.id === plan.id
                  ? "selected"
                  : ""
              } ${
                !plan.isAvailable
                  ? "disabled-plan"
                  : ""
              } ${
                isGuest ? "guest-disabled" : ""
              }`}
              onClick={() => {
                if (!clickable) {
                  alert(
                    "Guests can view plans, but must log in to subscribe."
                  );
                  return;
                }

                setSelectedPlan(plan);
                setShowPayPal(false);
              }}
              style={
                isGuest
                  ? {
                      cursor: "not-allowed",
                      opacity: 0.85,
                    }
                  : undefined
              }
            >
              <div className="plan-header">
                <div className="plan-header-text">
                  <h3>
                    {plan.name}{" "}
                    {!plan.isAvailable && (
                      <span className="coming-soon-badge">
                        Coming Soon
                      </span>
                    )}
                  </h3>

                  <p>{plan.description}</p>
                </div>
              </div>

              <div className="plan-price">
                CAD ${plan.subscriptionFee.toFixed(2)}/month
              </div>

              <div className="plan-annual">
                CAD ${annualPrice}/year &nbsp;
                <span className="discount">
                  ({discount}% off)
                </span>
              </div>
              <div className="plan-ai-benefits">
                <div className="plan-ai-title">Scoldy AI included</div>
                <div><strong>{plan.aiAskLimit}</strong> Ask Scoldy AI questions/month</div>
                <div><strong>{plan.aiResearchLimit}</strong> Scoldy AI Research requests/month</div>
              </div>

              <ul className="plan-features">
                <li>
                  <strong>{plan.postLimit}</strong>{" "}
                  characters/post
                </li>

                <li>
                  <strong>{plan.mediaLimit}</strong> KB media
                </li>

                <li>
                  <strong>{plan.editTimeout}</strong> min edit
                  window
                </li>

                <li>
                  Priority Boost{" "}
                  {plan.priorityBoost
                    ? `(${
                        plan.name === "Silver"
                          ? "1"
                          : plan.name === "Gold"
                          ? "2"
                          : plan.name === "Platinum"
                          ? "3"
                          : "4"
                      }/month)`
                    : "—"}
                </li>
              </ul>
            </div>
          );
        })}
      </div>

      <div className="cloud9-ai-explainer">
        <h2>AI included with every Premium plan</h2>
        <p><strong>Ask Scoldy AI</strong> — questions, ideas and explanations.</p>
        <p><strong>Scoldy AI Research</strong> — current information using AI and external sources, with sources identified.</p>
      </div>

      {/* ✅ Subscription / disclaimer section intact */}
      <div className="subscribe-section">
        {/* Never show PayPal container to guests */}
        {!isGuest &&
        showPayPal &&
        selectedPlan?.paypalPlanId ? (
          <>
            <div
              id="paypal-button-container"
              style={{ marginTop: "20px" }}
            ></div>

            {confirmingSubscription && (
              <p style={{ marginTop: "12px" }}>
                Confirming your subscription with Scoldy...
              </p>
            )}
          </>
        ) : (
          <button
            className="subscribe-button"
            onClick={handleSubscribe}
            disabled={isGuest}
            style={
              isGuest
                ? {
                    cursor: "not-allowed",
                    opacity: 0.7,
                  }
                : undefined
            }
            title={
              isGuest ? "Log in to subscribe" : undefined
            }
          >
            {isGuest
              ? "Log in to subscribe"
              : selectedPlan?.isAvailable
              ? "Subscribe to Selected Plan"
              : selectedPlan
              ? `${selectedPlan.name} is Coming Soon`
              : "Subscribe to Selected Plan"}
          </button>
        )}

        <p
          className="disclaimer"
          style={{ textAlign: "left" }}
        >
          Payments are processed securely via PayPal. Applicable
          taxes will be calculated at checkout. By subscribing to
          our plans you are confirming you have read and agree to
          our{" "}
          <a
            href="/terms-of-service.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms of Service
          </a>
          ,{" "}
          <a
            href="/scoldyprivacy.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/disclaimer.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Disclaimer
          </a>
          .
          <br />
          <br />
          <strong>Boost Included:</strong> Premium users can
          request to boost 1–4 posts per month, depending on their
          plan. Boosted posts are displayed prominently at the top
          of the home feed for all users for 24 hours or until
          replaced.
        </p>
      </div>
    </div>
  );
};

export default Cloud9;